import { css } from 'aphrodite';
import * as React from 'react';
import { Button } from '../../../../web/components-v2/Button';
import { Input } from '../../../../web/components-v2/Input';
import type { CoachLabNoteKind } from './labLogic';
import { styles } from './styles';

const NOTE_BUTTONS: { kind: CoachLabNoteKind; label: string; placeholder: string }[] = [
	{ kind: 'good', label: 'Good', placeholder: 'Why was this card good?' },
	{ kind: 'bad', label: 'Bad', placeholder: 'Why was this card wrong, and what should it have said?' },
	{ kind: 'note', label: 'Note', placeholder: 'For example: should have asked for the time here' },
	{ kind: 'prospect', label: "Prospect isn't realistic", placeholder: 'For example: no owner gives up that fast' },
];

/** The notes on one coach card, and the Good / Bad / Note buttons that add them. */
export const CardFeedback = ({
	notes,
	editable,
	kinds = ['good', 'bad', 'note', 'prospect'],
	prompt,
	onAdd,
	onRemove,
}: {
	notes: string[];
	editable: boolean;
	kinds?: CoachLabNoteKind[];
	prompt: string;
	onAdd: (kind: CoachLabNoteKind, text: string) => void;
	onRemove: (index: number) => void;
}) => {
	const [kind, setKind] = React.useState<CoachLabNoteKind | null>(null);
	const [draft, setDraft] = React.useState('');
	const buttons = NOTE_BUTTONS.filter(b => kinds.includes(b.kind));
	const active = buttons.find(b => b.kind === kind);

	const save = () => {
		if (kind && draft.trim()) onAdd(kind, draft);
		setDraft('');
		setKind(null);
	};

	return (
		<>
			{notes.map((n, i) => (
				<div key={i} className={css(styles.note)}>
					<span>{n}</span>
					{editable ? (
						<Button variant='link' size='sm' onClick={() => onRemove(i)}>
							Remove
						</Button>
					) : null}
				</div>
			))}
			{editable ? (
				<>
					<p className={css(styles.help)}>{prompt}</p>
					<div className={css(styles.actions)}>
						{buttons.map(b => (
							<Button
								key={b.kind}
								variant={kind === b.kind ? 'secondary' : 'outline'}
								size='sm'
								aria-pressed={kind === b.kind}
								onClick={() => setKind(kind === b.kind ? null : b.kind)}
							>
								{b.label}
							</Button>
						))}
					</div>
					{active ? (
						<div className={css(styles.actions)}>
							<Input
								autoFocus
								placeholder={active.placeholder}
								value={draft}
								onChange={e => setDraft(e.target.value)}
								onKeyDown={e => e.key === 'Enter' && save()}
							/>
							<Button variant='primary' size='sm' disabled={!draft.trim()} onClick={save}>
								Add {active.label.toLowerCase()}
							</Button>
						</div>
					) : null}
				</>
			) : null}
		</>
	);
};
